import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotifications } from '../../contexts/NotificationContext'
import BottomSheet from '../common/BottomSheet'
import { useHaptics } from '../../utils/haptics'
import PullToRefresh from '../common/PullToRefresh'
import { useMoney } from '../../hooks/usePublicSettings'
import { accountApi, jobsApi, statusLabel, timeAgo } from '../../utils/accountApi'

// Service icons
function FaucetIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M7 10h10a4 4 0 0 0-4-4V4"/>
      <path d="M19 10v4a2 2 0 0 1-2 2H7"/>
      <path d="M5 10h2v6H5z"/>
    </svg>
  )
}

function BoltIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M13 2L3 14h7l-1 8 10-12h-7z"/>
    </svg>
  )
}

function BrushIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M2 22s2-3 6-3 6 3 6 3"/>
      <path d="M14 2l8 8-6 6-8-8z"/>
    </svg>
  )
}

function SawIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M3 13l8 8 10-10-8-8z"/>
      <path d="M7 9l8 8"/>
    </svg>
  )
}

function HelmetIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 13a10 10 0 1 0-20 0"/>
      <path d="M2 13h20v5H2z"/>
    </svg>
  )
}

const DOT = { COMPLETED: 'bg-green-500', APPROVED: 'bg-green-500', CANCELLED: 'bg-red-500', REQUESTED: 'bg-yellow-500' }

export default function CustomerHome() {
  const { user } = useAuth()
  const { showSuccess, showError } = useNotifications()
  const navigate = useNavigate()
  const haptics = useHaptics()
  const money = useMoney()
  const [addressOpen, setAddressOpen] = useState(false)
  const [orders, setOrders] = useState([])
  const [activity, setActivity] = useState([])
  const [addresses, setAddresses] = useState([])
  const [loading, setLoading] = useState(true)

  const dateStr = new Date().toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  })

  const shortcuts = [
    { k: 'plumbing', label: 'Plumbing', icon: <FaucetIcon className="opacity-70"/> },
    { k: 'electrical', label: 'Electrical', icon: <BoltIcon className="opacity-70"/> },
    { k: 'painting', label: 'Painting', icon: <BrushIcon className="opacity-70"/> },
    { k: 'carpentry', label: 'Carpentry', icon: <SawIcon className="opacity-70"/> },
    { k: 'consultant', label: 'Consultant', icon: <HelmetIcon className="opacity-70"/> },
  ]

  const load = useCallback(async () => {
    const [o, a, ad] = await Promise.allSettled([jobsApi.orders(), jobsApi.activity(5), accountApi.addresses()])
    setOrders(o.status === 'fulfilled' ? o.value : [])
    setActivity(a.status === 'fulfilled' ? a.value : [])
    setAddresses(ad.status === 'fulfilled' ? ad.value : [])
    setLoading(false)
    return [o, a, ad].every(r => r.status === 'fulfilled')
  }, [])

  useEffect(() => { load() }, [load])

  const activeJobs = orders.filter(o => o.stage === 'active')
  const upcoming = orders.filter(o => o.stage === 'scheduled')
    .sort((a, b) => new Date(a.scheduled_start || 0) - new Date(b.scheduled_start || 0))
  const current = addresses.find(a => a.is_default) || addresses[0]

  const handleServiceShortcut = (serviceKey) => {
    haptics.light()
    navigate(`/services/${serviceKey}`)
  }

  const handleRefresh = async () => {
    haptics.light()
    const ok = await load()
    if (ok) { haptics.success(); showSuccess('Dashboard refreshed!') } else { haptics.error(); showError('Failed to refresh data') }
  }

  const chooseAddress = async (address) => {
    haptics.light()
    try {
      await accountApi.setDefaultAddress(address.id)
      setAddresses(await accountApi.addresses())
      setAddressOpen(false)
      showSuccess(`Using ${address.label || 'this address'}`)
    } catch (e) {
      showError(e.message)
    }
  }

  const fmtWhen = d => d ? new Date(d).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Time to be confirmed'

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white px-4 pt-6 pb-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xl font-bold text-slate-900">
              Hi {user?.name?.split(' ')[0] || 'Customer'},{' '}
              <span
                onClick={() => setAddressOpen(true)}
                className="cursor-pointer touch-manipulation hover:text-slate-700 transition-colors"
              >
                {current?.label || 'Add address'} <span className="text-sm text-slate-500">▼</span>
              </span>
            </div>
            <div className="text-sm text-slate-500">{dateStr}</div>
          </div>
        </div>
      </div>

      {/* Scrollable Content with Pull to Refresh */}
      <PullToRefresh onRefresh={handleRefresh}>
        <div className="px-4 py-6 space-y-6 pb-20">

        {/* Active Jobs - Only show when there are active jobs */}
        {activeJobs.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-base font-semibold mb-4 text-slate-900">Active Jobs</div>
            <div className="space-y-4">
              {activeJobs.map(job => (
                <div
                  key={job.job_id}
                  onClick={() => {
                    haptics.light()
                    navigate(`/jobs/${job.job_id}`)
                  }}
                  className="bg-slate-50 rounded-xl p-4 hover:bg-slate-100 transition-colors hover:shadow-md cursor-pointer touch-manipulation"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-slate-900">{job.title}</div>
                    <div className="text-sm font-medium text-slate-700">{statusLabel(job.job_status)}</div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-sm text-slate-600">{job.professional?.name || 'Professional to be assigned'}</div>
                    <div className="text-xs text-slate-500">Tap to view details</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Service Shortcuts - Primary way to find services */}
        <div className="flex gap-3 overflow-x-auto no-scrollbar py-2">
          {shortcuts.map(s => (
            <button
              key={s.k}
              onClick={() => handleServiceShortcut(s.k)}
              className="min-w-[100px] bg-white rounded-2xl p-4 text-sm flex flex-col items-center touch-manipulation hover:shadow-lg transition-all duration-200 shadow-md transform hover:-translate-y-1"
            >
              <span className="mb-2">{s.icon}</span>
              <span className="text-slate-700 font-medium">{s.label}</span>
            </button>
          ))}
        </div>

        {/* Browse Professionals Button - Secondary option */}
        <div className="bg-gradient-to-r from-slate-100 to-slate-50 rounded-2xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-1">Browse All Professionals</h3>
              <p className="text-slate-600 text-sm">View all available service providers</p>
            </div>
            <button
              onClick={() => {
                haptics.light()
                navigate('/professionals', { state: { fromBrowse: true } })
              }}
              className="px-4 py-2 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 touch-manipulation text-sm"
            >
              Browse
            </button>
          </div>
        </div>

        {/* Upcoming visits */}
        <div className="bg-white rounded-2xl p-5 shadow-lg hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="text-base font-semibold text-slate-900">Upcoming Visits</div>
            <button onClick={() => navigate('/scheduled')} className="text-xs text-slate-500 hover:text-slate-900">See all</button>
          </div>
          {upcoming.length === 0 ? (
            <div className="text-sm text-slate-500">No visits scheduled.</div>
          ) : (
            <div className="space-y-3">
              {upcoming.slice(0, 3).map(o => (
                <div key={o.job_id} onClick={() => navigate(`/jobs/${o.job_id}`)}
                  className="bg-slate-50 rounded-xl p-4 hover:bg-slate-100 transition-colors hover:shadow-md cursor-pointer touch-manipulation">
                  <div className="font-semibold text-slate-900">{o.title}</div>
                  <div className="text-sm text-slate-500 mt-1">{o.professional?.name || 'Professional to be assigned'}</div>
                  <div className="text-xs text-slate-400 mt-1">{fmtWhen(o.scheduled_start)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl p-5 shadow-lg hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="text-base font-semibold text-slate-900">Recent Activity</div>
            <button onClick={() => navigate('/orders')} className="text-xs text-slate-500 hover:text-slate-900">All orders</button>
          </div>
          {activity.length === 0 ? (
            <div className="text-sm text-slate-500">No activity yet. Request a service to get started.</div>
          ) : (
            <div className="space-y-3">
              {activity.map((e, i) => (
                <div
                  key={`${e.request_id}-${e.status}-${i}`}
                  onClick={() => {
                    haptics.light()
                    navigate(e.job_id ? `/jobs/${e.job_id}` : '/orders')
                  }}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 cursor-pointer touch-manipulation transition-colors"
                >
                  <div className={`w-2 h-2 ${DOT[e.status] || 'bg-blue-500'} rounded-full`}></div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900">{statusLabel(e.status)} · {e.title}</div>
                    <div className="text-xs text-slate-500">{timeAgo(e.at)}{e.amount != null ? ` · ${money(e.amount)}` : ''}</div>
                  </div>
                  <div className="text-xs text-slate-400">→</div>
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      </PullToRefresh>

      {/* Address Selection Bottom Sheet */}
      <BottomSheet open={addressOpen} onClose={() => setAddressOpen(false)} title="Select address">
        <div className="space-y-2">
          {addresses.length === 0 && <div className="text-sm text-slate-500">No saved addresses yet.</div>}
          {addresses.map(a => (
            <div key={a.id} className="border rounded-xl p-3 text-sm flex items-center justify-between">
              <div>
                <div className="font-semibold">{a.label || 'Address'}</div>
                <div className="text-[12px] text-slate-600">{[a.address_line_1, a.address_line_2, a.city].filter(Boolean).join(' · ')}</div>
              </div>
              {a.is_default
                ? <span className="text-xs px-2 py-1 text-green-700">In use</span>
                : <button onClick={() => chooseAddress(a)} className="text-xs px-2 py-1 rounded-lg border touch-manipulation">Use</button>}
            </div>
          ))}
          <button onClick={() => navigate('/addresses')} className="w-full py-2 rounded-xl border text-sm mt-2 touch-manipulation">+ Add new address</button>
        </div>
      </BottomSheet>
    </div>
  )
}
